import axios from 'axios';
import React from 'react';
import { IMplServiceClient } from '../api/IMplServiceClient';
import { MplServiceClientFactory } from '../api/MplServiceClientFactory';
import { IFeedbackServiceClient, FeedbackServiceFactory } from '../api/FeedbackService';
import PortalServiceClient from '../api/PortalServiceClient';
import { mplService } from '../api/Services';
import AuthenticationInfo from '../common/AuthenticationInfo';
import AuthenticationManager from '../common/AuthenticationManager';
import { AppContextData } from '../contexts/AppContext';
import { log } from '../loggers/Logger';
import VersionInfo from '../model/VersionInfo';
import AppProviders from './AppProviders';
import Contents from './common/Contents';
import LoadingPage from './common/LoadingPage';
import MaintenanceInfo from './common/MaintenanceInfo';
import UnAuthContents from './common/UnAuthContents';
import BrowserHistory from './utils/BrowserHistory';
import { loadEntities } from './utils/EntitiesUtils';
import { setTextEntities } from '@amzn/awscat-editor';

interface AppState extends AppContextData {
  isInitialized: boolean;
}

class App extends React.Component<any, AppState> {
  private authenticationManager: AuthenticationManager;
  private mplServiceClient: IMplServiceClient;
  private mplServiceClientV2: IMplServiceClient;
  private portalServiceClient: PortalServiceClient;
  private feedbackServiceClient: IFeedbackServiceClient;

  constructor(props: any) {
    super(props);
    this.mplServiceClient = MplServiceClientFactory.create();
    this.mplServiceClientV2 = MplServiceClientFactory.createV2();
    this.portalServiceClient = PortalServiceClient.create();
    this.authenticationManager = AuthenticationManager.create();
    this.feedbackServiceClient = FeedbackServiceFactory.create();

    this.authenticationManager.configure(this.signIn, this.redirectToSignInPage);

    this.state = {
      isAuthenticated: false,
      isInitialized: false,
      sideNavStatus: true,
    };
  }

  public async componentDidMount() {
    await this.getVersionInfo();
    await this.authenticateUser();
    await this.loadEntities();
    this.setState({
      isInitialized: true,
    });
  }

  public async componentDidUpdate(prevProps: any, prevState: AppState) {
    const { isAuthenticated: wasAuthenticated } = prevState;
    const { isAuthenticated } = this.state;
    if (!wasAuthenticated && isAuthenticated) {
      this.setState({
        isInitialized: true,
      });
    }
  }

  public render() {
    const { isInitialized, versionInfo, isAuthenticated } = this.state;

    if (!isInitialized) {
      return <LoadingPage />;
    }

    return (
      <AppProviders appContext={this.state}>
        {/* @ts-expect-error TS18048 - strict migration */}
        {versionInfo.maintenanceMode ? (
          <MaintenanceInfo versionInfo={versionInfo} />
        ) : isAuthenticated ? (
          <Contents />
        ) : (
          <UnAuthContents />
        )}
      </AppProviders>
    );
  }

  private async authenticateUser() {
    if (!location.pathname.startsWith('/login')) {
      await this.authenticationManager.refreshToken();
    }
  }

  private async getVersionInfo() {
    const res = await axios.get('/version.json');
    this.setState({
      versionInfo: res.data as VersionInfo,
    });
  }

  private signIn = async (authnInfo: AuthenticationInfo) => {
    const { userProfile, tokenInfo } = authnInfo;
    mplService.defaults.headers.common.Authorization = `Bearer ${tokenInfo.token}`;
    mplService.defaults.headers.common['x-auth-provider'] = 'catportal';
    this.mplServiceClient.setAuthentication(tokenInfo.token);
    this.mplServiceClientV2.setAuthentication(tokenInfo.token);
    const mplUserProfile = await this.mplServiceClient.getCurrentUserProfile();
    userProfile.roles = mplUserProfile.roles;
    userProfile.contentRoles = mplUserProfile.contentRoles;
    // Log the initial page
    log.info({ location: location.pathname, type: 'LOCATION_CHANGED' });
    this.setState({
      authnInfo,
      isAuthenticated: true,
    });
  };

  private redirectToSignInPage = async () => {
    this.portalServiceClient.redirectToLoginPage(BrowserHistory.location.pathname);
  };

  private loadEntities = async () => {
    const entities = await loadEntities();

    // Immediately register the entities with the Tiptap extensions; without
    // this, the getText() utility function won't perform entity replacement
    // until the Tiptap editor is loaded.
    // @ts-expect-error TS2345 - strict migration
    setTextEntities(entities);

    this.setState({
      entities,
    });
  };
}

export default App;
